<template>
  <aside class="variant-selector" v-if="selectedVariant">
    <img :src="selectedVariant.image.url" loading="lazy" :alt="localProduct.title" class="variant-selector__img">
    <div class="variant-selector__content">
      <div class="variant-selector__title">
        <p>{{ localProduct.title }}</p>
        <p>{{ formatMoney(selectedVariant.price.amount * 100, moneyFormat) }}</p>
      </div>

      <!-- Existing code in <template> -->
      <p v-if="colourOptions && !hasAlternateColours" class="variant-selector__options-title">{{ colourOptions.name }}</p>

      <div v-if="colourOptions && !hasAlternateColours" class="variant-selector__colour-options">
        <div class="variant-selector__colours-container" v-for="colour in colourOptions.values" :key="colour">
          <button style="padding: 0;" class="variant-selector__colour" type="button"
            :class="activeColour && activeColour.value === colour ? 'active' : ''" 
            @click="updateOption(colourName, colour)">
            <img class="variant-selector__colour-option" :src="getVariantImage(colour)" alt="">
          </button>
        </div>
      </div>

      <p class="info" v-if="activeColour && !hasAlternateColours">{{ $t('products.product.colour') }} {{ activeColour.value }}</p>

      <!-- Display alternate colours if available -->
      <p v-if="hasAlternateColours" class="variant-selector__options-title">{{ $t('products.product.choose_colour') }}</p>
      <div v-if="hasAlternateColours" class="variant-selector__colour-options">
        <div class="variant-selector__colours-container" v-for="alternate in sortedAlternateColours" :key="alternate.id">
          <button style="padding:0" @click.prevent="selectAlternateProduct(alternate)" type="button" class="variant-selector__colour"
            :class="{ 'active': selectedAlternateColour && selectedAlternateColour === alternate.id }">
            <img class="variant-selector__colour-option" :src="alternate.featuredImage.url" :alt="alternate.title">
          </button>
        </div>
        <div class="product-meta recommendations-meta u-1/1@tablet">
          <span class="product-meta__title">{{ $t('products.product.colour') }}</span>
          <span class="product-meta__content" data-product-color>{{ selectedVariant.selectedOptions.find(option => option.name === 'Colour').value }}</span>
        </div>
      </div>

      <!-- New Other Option Section -->
      <div v-if="otherOptions.length > 0">
        <p class="variant-selector__options-title">{{ otherOptionName }}</p>
        <div class="variant-selector__size-options variant-selector__other-options radio-button">
          <button 
            type="button" 
            v-for="option in otherOptions" 
            class="radio-button__label" 
            :key="option.value"
            @click="updateOption(otherOptionName, option.value, option.isOutOfStock)" 
            :class="{
              'active': activeOtherOption && activeOtherOption.value === option.value
            }"
            :data-out-of-stock="option.isOutOfStock"
          >
            {{ option.value }}
          </button>
        </div>
      </div>

      <div v-for="(optionSet, index) in sizeOptions" :key="optionSet.name">
  <p v-if="optionSet.sizes.length > 0" class="variant-selector__options-title">
    {{ optionSet.name }}
  </p>
  <div class="variant-selector__size-options radio-button" v-if="optionSet.sizes.length > 0">
    <button 
      type="button" 
      v-for="size in optionSet.sizes" 
      class="radio-button__label" 
      :key="size.value"
      @click="updateOption(optionSet.name, size.value)" 
      :class="{
        'active': isActiveSize(optionSet.name, size.value)
      }"
      :data-out-of-stock="size.isOutOfStock"
    >
      {{ size.value }}
    </button>
  </div>
</div>

      <div 
        class="variant-selector__quantities"
        :class="{ 'u-hide': isOutOfStock }"
      >
        <p>{{ $t('products.product.quantity') }} </p>
        <select class="variant-selector__quantity" v-model="selectedQuantity">
          <option v-for="(quantity, index) in quantities" :key="index" :value="quantity">
            {{ quantity }}
          </option>
        </select>
      </div>

      <div>
        <button 
          @click="addToCart()" 
          type="button"
          class="variant-selector__button button button--primary button--rounded button--fill"
          :class="{ 'u-hide': isOutOfStock }"
          :disabled="isOutOfStock"
          data-add-to-cart-recommendation
        >
        {{ $t('products.product.add_to_cart') }}
        </button>
        <button
          @click="backInStock(selectedVariant.product.id, selectedVariant.id)"
          class="recommended-email-me__button button button--primary button--rounded button--fill"
          data-micromodal-trigger="back-in-stock"
          data-product-action
          type="button"
          :class="{ 'u-hide': !isOutOfStock }"
          :disabled="!isOutOfStock"
          :data-product-id="selectedVariant.product.id"
          :data-variant-id="selectedVariant.id"
        >
        {{ $t('products.product.back_in_stock.email_me') }}
        </button>
        <a :href="`/products/${product.handle}`" class="
      
                    button
                    button--tertiary
                    button--rounded
                    button--fill
                    button--icon">{{ $t('products.product.view_product') }}</a>
      </div>
    </div>

    <button class="variant-selector__close" style="position: " type="button" @click="emit('close-menu')">

      <svg aria-hidden="true" focusable="false" role="presentation" class="icon icon-close" viewBox="0 0 40 40">
        <path
          d="M23.868 20.015L39.117 4.78c1.11-1.108 1.11-2.77 0-3.877-1.109-1.108-2.773-1.108-3.882 0L19.986 16.137 4.737.904C3.628-.204 1.965-.204.856.904c-1.11 1.108-1.11 2.77 0 3.877l15.249 15.234L.855 35.248c-1.108 1.108-1.108 2.77 0 3.877.555.554 1.248.831 1.942.831s1.386-.277 1.94-.83l15.25-15.234 15.248 15.233c.555.554 1.248.831 1.941.831s1.387-.277 1.941-.83c1.11-1.109 1.11-2.77 0-3.878L23.868 20.015z"
          class="layer" />
      </svg>
    </button>
  </aside>
</template>

<script setup>
import { computed, defineEmits, defineProps, onMounted, onUnmounted, ref } from 'vue';
import * as Cart from "../entrypoints/utils/cart";
import { formatMoney } from "@shopify/theme-currency";
import MicroModal from 'micromodal';

const props = defineProps(['product', 'id', 'options', 'alternateColours']);
const emit = defineEmits(['close-menu']);

const variants = ref([]);
const selectedOptions = ref(props.options);
const selectedVariant = ref();
const selectedQuantity = ref(1);
const moneyFormat = ref();
const isOutOfStock = ref(false);
const alternateColours = ref(props.alternateColours || []);
const selectedAlternateColour = ref(null);
const localProduct = ref(props.product);

const sortedAlternateColours = computed(() => {
  return [...alternateColours.value].sort((a, b) => {
    return a.title.localeCompare(b.title);
  });
});

const quantities = Array.from({ length: 99 }, (_, i) => i + 1);

const activeSizes = computed(() => {
  return selectedOptions.value.filter(option => option.name.toLowerCase().includes('size'));
});

const isActiveSize = (optionName, sizeValue) => {
  return activeSizes.value.some(size => size.name === optionName && size.value === sizeValue);
};

const activeColour = computed(() => {
  return selectedOptions.value.find(option => option.name === 'Colour');
});

const activeOtherOption = computed(() => {
  return selectedOptions.value.find(option => !option.name.includes('Size') && !option.name.includes('Colour'));
});

const isVariantOutOfStock = (optionName, optionValue) => {
  const currentOptions = selectedOptions.value.map(option => 
    option.name === optionName ? { name: optionName, value: optionValue } : option
  );

  const variant = variants.value.find(variant =>
    currentOptions.every(option =>
      variant.selectedOptions.some(
        variantOption => variantOption.name === option.name && variantOption.value === option.value
      )
    )
  );

  return variant ? variant.quantityAvailable < 1 : true;
};

const updateOption = async (optionName, option) => {
  selectedOptions.value = selectedOptions.value.map(selectedOption => {
    if (selectedOption.name === optionName) {
      return { name: optionName, value: option };
    }
    return selectedOption;
  });

  // If the option doesn't exist in selectedOptions, add it
  if (!selectedOptions.value.some(o => o.name === optionName)) {
    selectedOptions.value.push({ name: optionName, value: option });
  }

  if (variants.value.length === 0) {
    console.error('No variants found for the product.');
    return;
  }

  selectedVariant.value = variants.value.find(variant =>
    selectedOptions.value.every(option =>
      variant.selectedOptions.some(
        variantOption => variantOption.name === option.name && variantOption.value === option.value
      )
    )
  );

  if (!selectedVariant.value) {
    console.error(`No matching variant found for ${JSON.stringify(selectedOptions.value)}`);
  }

  isOutOfStock.value = selectedVariant.value ? selectedVariant.value.quantityAvailable < 1 : true;

  // Force update of computed properties
  selectedOptions.value = [...selectedOptions.value];
};

const getVariantImage = (colour) => {
  const variant = variants.value.find(variant =>
    variant.selectedOptions.some(option => option.name === 'Colour' && option.value === colour)
  );
  return variant ? variant.image.url : null;
};

const selectAlternateProduct = async (alternate) => {
  try {
    const { product: altProduct, variants: altVariants } = await fetchProductDetails(alternate.id);

    localProduct.value = altProduct;
    variants.value = altVariants;

    // Update the selected variant and options to the first variant's options
    selectedOptions.value = altProduct.options.map(option => ({
      name: option.name,
      value: option.values[0]
    }));

    selectedVariant.value = altVariants.find(variant =>
      selectedOptions.value.every(option =>
        variant.selectedOptions.some(
          variantOption => variantOption.name === option.name && variantOption.value === option.value
        )
      )
    );

    // Update isOutOfStock based on the selected variant
    isOutOfStock.value = selectedVariant.value ? selectedVariant.value.quantityAvailable < 1 : false;

    // Update alternate colours and selected colour
    alternateColours.value = altProduct.alternateColours || [];
    selectedAlternateColour.value = alternate.id;

    // Emit update event
    emit('update-product', altProduct, selectedOptions.value, alternateColours.value);

  } catch (error) {
    console.error(`Error selecting alternate product: ${error}`);
  }
};

const fetchProductDetails = async (productId) => {
  const currentCountry = Shopify.country.toUpperCase();
  const query = `
    query getProductDetails @inContext(country: ${currentCountry}) {
      product(id: "${productId}") {
        id
        title
        handle
        featuredImage {
          url
        }
        options {
          name
          values
        }
        variants(first: 100) {
          edges {
            node {
              id
              title
              quantityAvailable
              selectedOptions {
                name
                value
              }
              product {
                id
              }
              price {
                amount
                currencyCode
              }
              image {
                url
              }
            }
          }
        }
        metafield(key: "alternate_colours", namespace: "custom") {
          value
          references(first: 10) {
            edges {
              node {
                ... on Product {
                  id
                  handle
                  title
                  featuredImage {
                    url
                  }
                  variants(first: 1) {
                    edges {
                      node {
                        id
                        product {
                          id
                        }
                        title
                        quantityAvailable
                        selectedOptions {
                          name
                          value
                        }
                        price {
                          amount
                          currencyCode
                        }
                        image {
                          src
                        }
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  `;

  const response = await fetchFromStorefront(query);
  const productData = response.data.product;
  const altColours = productData.metafield ? productData.metafield.references.edges.map(edge => edge.node) : [];
  const variants = productData.variants.edges.map(edge => edge.node);

  return {
    product: {
      id: productData.id,
      title: productData.title,
      handle: productData.handle,
      featuredImage: productData.featuredImage,
      options: productData.options,
      alternateColours: altColours
    },
    variants
  };
};

const fetchFromStorefront = async (query) => {
  const response = await fetch(`/api/2024-04/graphql.json`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-Shopify-Storefront-Access-Token": import.meta.env.VITE_SHOPIFY_STOREFRONT_API_KEY,
    },
    body: JSON.stringify({ query }),
  });
  return response.json();
};

const hasAlternateColours = computed(() => alternateColours.value.length > 0);

const colourOptions = computed(() => {
  return localProduct.value.options ? localProduct.value.options.find(option => option.name === 'Colour') : null;
});

const sizeOptions = computed(() => {
  const sizeRelatedOptions = props.product.options ? props.product.options.filter(option => option.name.toLowerCase().includes('size')) : [];
  if (sizeRelatedOptions.length === 0) return [];

  return sizeRelatedOptions.map(sizeOption => {
    return {
      name: sizeOption.name,
      sizes: sizeOption.values.map(sizeValue => {
        return {
          value: sizeValue,
          isOutOfStock: isVariantOutOfStock(sizeOption.name, sizeValue)
        };
      })
    };
  });
});

const sizeName = computed(() => {
  const sizeOption = props.product.options ? props.product.options.find(option => option.name.includes('Size')) : null;
  return sizeOption ? sizeOption.name : 'Size';
});

const colourName = computed(() => {
  const colourOption = props.product.options ? props.product.options.find(option => option.name === 'Colour') : null;
  return colourOption ? colourOption.name : 'Colour';
});

const otherOptions = computed(() => {
  const options = props.product.options ? props.product.options.find(option => !option.name.toLowerCase().includes('size') && option.name !== 'Colour') : null;
  if (!options) return [];

  const activeColorOption = activeColour.value;
  const activeSizeValues = activeSizes.value.map(size => size.value);

  return options.values.map(optionValue => {
    // Find the variant that matches the current sizes, color, and other option
    const variant = variants.value.find(variant => {
      const sizesMatch = activeSizeValues.every(sizeValue => 
        variant.selectedOptions.some(option => option.name.toLowerCase().includes('size') && option.value === sizeValue)
      );
      const optionMatch = variant.selectedOptions.some(option => 
        !option.name.toLowerCase().includes('size') && 
        option.name !== 'Colour' && 
        option.value === optionValue
      );
      const colorMatch = activeColorOption ? variant.selectedOptions.some(option => 
        option.name === 'Colour' && option.value === activeColorOption.value
      ) : true;
      return sizesMatch && optionMatch && colorMatch;
    });

    const isOutOfStock = variant ? variant.quantityAvailable < 1 : false;
    
    return {
      value: optionValue,
      isOutOfStock: isOutOfStock
    };
  });
});




const otherOptionName = computed(() => {
  const otherOption = props.product.options ? props.product.options.find(option => !option.name.includes('Size') && !option.name.includes('Colour')) : null;
  return otherOption ? otherOption.name : 'Other Option';
});

const getVariants = async (productId, selectedOptions) => {
  const currentCountry = Shopify.country.toUpperCase();
  const query = `
    query getVariants @inContext(country: ${currentCountry}) {
      product(id: "${productId}") {
        variants(first: 100) {
          edges {
            node {
              id
              title
              quantityAvailable
              selectedOptions {
                name
                value
              }
              product {
                id
              }
              price {
                amount
                currencyCode
              }
              image {
                url
              }
            }
          }
        }
      }
    }
  `;

  const STOREFRONT_API_VERSION = "2024-04";

  const STOREFRONT_API_URL = `/api/${STOREFRONT_API_VERSION}/graphql.json`;

  /** @type {RequestInit} */
  const requestDefaults = {
    async: true,
    crossDomain: true,
    method: "POST",
    headers: {
      "X-Shopify-Storefront-Access-Token": import.meta.env.VITE_SHOPIFY_STOREFRONT_API_KEY,
      "Content-Type": "application/graphql",
    },
  };

  const fetchFromStorefront = (body) => {
    return fetch(STOREFRONT_API_URL, {
      ...requestDefaults,
      body,
    }).then((response) => response.json());
  };

  const response = await fetchFromStorefront(query);

  variants.value = response.data.product.variants.edges.map(edge => edge.node);

  selectedVariant.value = variants.value.find(variant =>
    selectedOptions.every(option =>
      variant.selectedOptions.some(
        variantOption => variantOption.name === option.name && variantOption.value === option.value
      )
    )
  );
};

const addToCart = async () => {
  let id = selectedVariant.value.id.split("/").pop();
  const addToCart = await Cart.add([
    {
      id,
      quantity: selectedQuantity.value,
     properties: {
       Message: "",
     }
    },
  ]);

  const response = await addToCart.json();

  if (response.items && response.items[0]) {
    const productData = response.items[0];

    window.dispatchEvent(
      new CustomEvent("cart.added", { detail: { productData } })
    );

    setTimeout(() => {
      emit('close-menu');
    }, 200);
  } else {
    console.error(`Failed to add item to cart: ${response}`);
  }
};

const backInStock = (productId, variantId) => {

  setTimeout(() => {
    emit('close-menu');
    const bisTrigger = document.querySelector('[data-manual-bis-button]');
    if (bisTrigger) {
      bisTrigger.click();
      const form = document.querySelector('#back-in-stock-content');
      if (form) {
        // Update the input with name="product"
        const productInput = form.querySelector('input[name="product"]');
        if (productInput) {
          const strippedProductId = productId.replace('gid://shopify/Product/', '');
          productInput.value = strippedProductId;
        }

        // Update the input with name="variant"
        const variantInput = form.querySelector('input[name="variant"]');
        if (variantInput) {
          const strippedVariantId = variantId.replace('gid://shopify/ProductVariant/', '');
          variantInput.value = strippedVariantId;
        }
      }
    } else {
      console.error('Failed to trigger BIS modal.');
    }
  }, 200);
};

onMounted(() => {
  document.body.classList.add(`u-overflow-hidden`);
  moneyFormat.value = window.theme.moneyFormat;

  localProduct.value = props.product;
  
  // Initialize selectedOptions with all product options
  selectedOptions.value = props.product.options.map(option => ({
    name: option.name,
    value: option.values[0] // Default to first value
  }));

  getVariants(props.id, selectedOptions.value).then(() => {
    if (variants.value.length > 0) {
      selectedVariant.value = variants.value.find(variant =>
        selectedOptions.value.every(option =>
          variant.selectedOptions.some(
            variantOption => variantOption.name === option.name && variantOption.value === option.value
          )
        )
      ) || variants.value[0];

      isOutOfStock.value = selectedVariant.value ? selectedVariant.value.quantityAvailable < 1 : false;
    }
  });

  if (alternateColours.value.length > 0) {
    selectedAlternateColour.value = alternateColours.value[0].id;
  }
});

onUnmounted(() => {
  document.body.classList.remove(`u-overflow-hidden`);
});
</script>

<style scoped>
.variant-container {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 60;

  -webkit-backdrop-filter: blur(0.625rem);
    backdrop-filter: blur(0.625rem);
    background-color: hsla(0, 0%, 7%, .20);
}

.variant-selector {
  font-size:1.6rem; /* reset aside to 1rem = 16px */
  overflow: scroll;
  position: relative;
  background: white;
  bottom: 0;
  box-shadow: 0 0 1rem #c3c3c3;
  color: #1B1C1E;

  flex-direction: column;
  height: 100%;
  position: fixed;
  right: 0;
  outline: none;
  top: 0;
  
  -webkit-user-select: none;
  -moz-user-select: none;
  user-select: none;
  width: 32rem;
  z-index: 60;

  @media (min-width: 64em) {
    width: 38rem;
  }
}

.variant-selector * {
  font-size:inherit; /* inherit reset so 1rem = 16px */
}

.variant-selector__close {
  position: absolute;
  top: 0.625rem;
  right: 0.625rem;
  background: white;
  height: 3rem;
  width: 3rem;
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
  padding:0;
}

.variant-selector__close svg {
  height: 1.25rem;
  width: 1.25rem;
}

.variant-selector__img {
  aspect-ratio: 1/1;
}

.variant-selector__content {
  padding: 1.5rem;

  @media (min-width: 64em) {
    padding: 2.5rem;
  }
}

.variant-selector__title {
  font-weight: 500;
  display: flex;
  justify-content: space-between;
  margin-bottom: 1.5rem;
}

.variant-selector__colour-options {
  display: flex;
  gap: 0.75rem;
  flex-wrap: wrap;
  margin-bottom: 0.5rem;
}

.variant-selector__size-options {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 12px;
  margin-bottom: 1.5rem;
}

.variant-selector__options-title {
  font-weight: 500;
  font-size: 1.4rem;
  margin-bottom: 0.5rem;
}

.variant-selector__colour-option {
  aspect-ratio: 1/1;
  width: 100%;
  height: 100%;
}

.variant-selector__colours-container {
  display: flex;
}

.variant-selector__colour {
  width: 4.8rem;
}

.variant-selector__button, .recommended-email-me__button {
  margin-bottom: 1.5rem;
}

.variant-selector :focus+.radio-button__label,
.radio-button__label:focus,
.radio-button--focused .radio-button__label {
  box-shadow: unset;
}

.radio-button__label.active {
  border-color: #0F274D;
}

.variant-selector__colour.active {
  border-color: #0F274D;
    border: 1px solid;
}

.variant-selector .info {
  font-size: 1.4rem;
  margin-bottom: 1.5rem;
}

.variant-selector__quantities {
  font-size: 1.4rem;
  display: flex;
  align-items: center;
  margin-bottom: 1rem;
}

.variant-selector__quantities.u-hide {
  display:none
}

.v-enter-active,
.v-leave-active {
  transition: opacity 0.5s ease;
}

.v-enter-from {
  transform: translateX(100%);
}
.v-leave-to {
  transform: translateX(0);
}
</style>